import { useRef, useState } from "react";
import { Camera, FolderOpen, ImagePlus, MapPin, Monitor, Smartphone } from "lucide-react";
import { toast } from "sonner";
import { useUploads } from "@/context/UploaderContext";
import { useAuth } from "@/context/AuthContext";
import { fromDrop, fromPicker } from "@/lib/folderFiles";
import { FolderUploadDialog } from "./FolderUploadDialog";
import { AlbumChoice } from "@/components/library/AlbumChoice";
import { WhoCanSee } from "./WhoCanSee";

const UA = typeof navigator !== "undefined" ? navigator.userAgent : "";
export const IS_IOS = /iPhone|iPad|iPod/i.test(UA) || (typeof navigator !== "undefined" && navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
export const IS_ANDROID = /Android/i.test(UA);
const PHONE = IS_IOS || IS_ANDROID;
const ACCEPT = "image/*,video/*,.heic,.heif,.mov,.mp4,.m4v";

// "From this phone" (native picker + camera) or "From this computer" (drag & drop + Choose files)
export const DeviceSource = ({ dest, setDest, albumId, setAlbumId, needAlbum }) => {
  const { add } = useUploads();
  const pickRef = useRef(null);
  const camRef = useRef(null);
  const folderRef = useRef(null);
  const { user } = useAuth();
  const canFolder = user?.role === "admin" || user?.role === "family";
  const [picked, setPicked] = useState(null);
  const [drag, setDrag] = useState(false);
  const onDrop = async (e) => {
    e.preventDefault();
    setDrag(false);
    const files = e.dataTransfer.files;
    const tree = canFolder ? await fromDrop(e.dataTransfer) : null;
    if (tree) setPicked(tree.length ? tree : null);
    else onFiles(files);
  };
  const target = (
    <div className="device-target" data-testid="device-target">
      <AlbumChoice value={albumId} onChange={setAlbumId} onSpace={setDest} newSpace={dest} testid="import-album" />
      <WhoCanSee space={dest} onChange={setDest} locked={!!albumId} />
    </div>
  );
  const guard = (fn) => () => (needAlbum ? toast.error("Choose an album first (or create a new one)") : fn());
  const onFiles = (files) => {
    if (!files?.length) return;
    if (needAlbum) return toast.error("Choose an album first (or create a new one)");
    add(files, dest, albumId);
    toast.success(`Uploading ${files.length} file${files.length === 1 ? "" : "s"}`);
  };
  const inputs = (
    <>
      <input ref={pickRef} type="file" multiple accept={PHONE ? "image/*,video/*" : ACCEPT} className="hidden" onChange={(e) => { onFiles(e.target.files); e.target.value = ""; }} data-testid="upload-file-input" />
      <input ref={camRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => { onFiles(e.target.files); e.target.value = ""; }} data-testid="upload-camera-input" />
    </>
  );
  if (PHONE) {
    return (
      <section className="source-card" data-testid="source-device">
        <button type="button" className="source-main" onClick={guard(() => pickRef.current?.click())} data-testid="source-device-button">
          <Smartphone size={26} strokeWidth={1.5} /><span><b>From this phone</b><small>Pick photos & videos</small></span>
        </button>
        <button type="button" className="source-sub" onClick={guard(() => camRef.current?.click())} data-testid="source-camera-button"><Camera size={18} /> Take photo</button>
        {target}
        {IS_IOS && <p className="mobile-tip !flex" data-testid="upload-location-tip-banner"><MapPin size={14} className="shrink-0 mt-0.5" /> On iPhone, tap Options and turn on Location before uploading to keep photo locations.</p>}
        {IS_ANDROID && <p className="android-tip" data-testid="upload-android-tip"><MapPin size={14} className="shrink-0 mt-0.5" /> Android may remove photo locations when uploading from a browser. To keep locations, upload from a computer.</p>}
        {inputs}
      </section>
    );
  }
  return (
    <section className={`source-card dropzone-card ${drag ? "drag" : ""}`} data-testid="source-device"
      onDragOver={(e) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)}
      onDrop={onDrop}>
      <div className="source-main !cursor-default" data-testid="upload-drop-zone">
        <Monitor size={26} strokeWidth={1.5} />
        <span><b>From this computer</b><small>Drop photos, videos{canFolder ? " or whole folders" : ""} here · JPEG, HEIC, PNG, MP4, MOV · videos up to 600 MB</small></span>
      </div>
      <div className="device-row">
        <div className="flex flex-col gap-2 self-start shrink-0">
          <button type="button" className="btn-primary inline-flex items-center gap-2" onClick={guard(() => pickRef.current?.click())} data-testid="source-choose-files-button"><ImagePlus size={16} /> Choose files</button>
          {canFolder && <button type="button" className="btn-outline inline-flex items-center gap-2" onClick={() => folderRef.current?.click()} data-testid="source-choose-folder-button"><FolderOpen size={16} /> Choose folder</button>}
        </div>
        {target}
      </div>
      {inputs}
      {canFolder && (
        <>
          <input ref={folderRef} type="file" webkitdirectory="" directory="" multiple className="hidden" data-testid="upload-folder-input"
            onChange={(e) => { const l = fromPicker(e.target.files); e.target.value = ""; if (l.length) setPicked(l); }} />
          <FolderUploadDialog picked={picked} dest={dest} onClose={() => setPicked(null)} />
        </>
      )}
    </section>
  );
};
